import React, { useCallback, useEffect, useState } from "react";
import { Badge, Card, Col, Row, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IIncident } from "../../shared/interfaces/incident/IIncident";
import { ExternalIncidentService } from "./service";
import { handleError } from "../../shared/functions/handleError";
import { useIncidentsListWebSocket } from "../../shared/hooks/useWebSocket";

const getStatusBadge = (status: string) => {
    switch (status) {
        case "PENDING":
            return "warning";
        case "IN_PROGRESS":
            return "info";
        case "RESOLVED":
            return "success";
        case "CANCELLED":
        case "REVOKED":
            return "secondary";
        default:
            return "primary";
    }
};

const getIncidentTypeColor = (type: string): string => {
    switch (type) {
        case "ACTIVE_SHOOTER":
        case "KNIFE_ATTACK":
        case "VIOLENT_ACTIVITY":
        case "SECURITY_THREAT":
            return "#dc3545";
        case "FIRE_EMERGENCY":
        case "FIRE":
            return "#fd7e14";
        case "MEDICAL":
            return "#0dcaf0";
        case "NATURAL_DISASTER":
        case "HAZMAT":
            return "#6c757d";
        default:
            return "#0d6efd";
    }
};

const getIncidentTypeIcon = (type: string): string => {
    switch (type) {
        case "ACTIVE_SHOOTER":
        case "KNIFE_ATTACK":
        case "VIOLENT_ACTIVITY":
            return "ph-duotone ph-warning-diamond";
        case "FIRE_EMERGENCY":
        case "FIRE":
            return "ph-duotone ph-fire";
        case "MEDICAL":
            return "ph-duotone ph-first-aid-kit";
        case "NATURAL_DISASTER":
            return "ph-duotone ph-cloud-lightning";
        case "HAZMAT":
            return "ph-duotone ph-biohazard";
        default:
            return "ph-duotone ph-warning";
    }
};

const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString();
};

const PoliceDashboard = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [incidents, setIncidents] = useState<IIncident[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const isActive = (i: IIncident) =>
        i.statusType === "PENDING" || i.statusType === "IN_PROGRESS";

    // Alerting is global (useIncidentAlerts); here we only refresh the list
    const fetchIncidents = useCallback(() => {
        ExternalIncidentService.getAll()
            .then((response) => {
                const data = Array.isArray(response)
                    ? response
                    : Array.isArray((response as { data: IIncident[] })?.data)
                        ? (response as { data: IIncident[] }).data
                        : [];
                setIncidents(data);
            })
            .catch(handleError)
            .finally(() => setIsLoading(false));
    }, []);

    const handleIncidentNotification = useCallback(() => {
        fetchIncidents();
    }, [fetchIncidents]);

    useIncidentsListWebSocket(handleIncidentNotification);

    useEffect(() => {
        fetchIncidents();
    }, [fetchIncidents]);

    const activeIncidents = incidents.filter(isActive);
    const resolvedIncidents = incidents.filter(
        (i) => i.statusType === "RESOLVED" || i.statusType === "CANCELLED" || i.statusType === "REVOKED",
    );

    return (
        <div className="police-dashboard">
            {/* Header */}
            <div className="d-flex align-items-center justify-content-between mb-4">
                <div>
                    <h3 className="mb-1">
                        <i className="ph-duotone ph-shield-star me-2 text-primary" />
                        {t("policeDashboard.title")}
                    </h3>
                    <small className="text-muted d-flex align-items-center gap-1">
                        <span
                            className="rounded-circle bg-success d-inline-block"
                            style={{ width: 8, height: 8 }}
                        />
                        {t("policeDashboard.liveUpdates")}
                    </small>
                </div>
                <Badge bg="danger" className="fs-6 px-3 py-2">
                    <i className="ph-duotone ph-warning me-1" />
                    {activeIncidents.length} {t("policeDashboard.activeIncidents")}
                </Badge>
            </div>

            {isLoading ? (
                <div className="d-flex justify-content-center align-items-center py-5">
                    <Spinner animation="border" variant="primary" />
                </div>
            ) : incidents.length === 0 ? (
                <Card className="text-center py-5">
                    <Card.Body>
                        <i
                            className="ph-duotone ph-shield-check"
                            style={{ fontSize: 64, color: "#28a745", opacity: 0.5 }}
                        />
                        <h5 className="mt-3 text-muted">{t("policeDashboard.noIncidents")}</h5>
                        <p className="text-muted">{t("policeDashboard.noIncidentsDescription")}</p>
                    </Card.Body>
                </Card>
            ) : (
                <>
                    {activeIncidents.length > 0 && (
                        <div className="mb-4">
                            <h5 className="text-danger mb-3">
                                <i className="ph-duotone ph-warning me-1" />
                                {t("policeDashboard.activeSection")}
                            </h5>
                            <Row className="g-3">
                                {activeIncidents.map((incident) => (
                                    <Col key={incident.id} xl={3} lg={4} md={6} sm={12}>
                                        <IncidentCard
                                            incident={incident}
                                            onClick={() => navigate(`/incidents/${incident.id}`)}
                                        />
                                    </Col>
                                ))}
                            </Row>
                        </div>
                    )}

                    {resolvedIncidents.length > 0 && (
                        <div>
                            <h5 className="text-muted mb-3">
                                <i className="ph-duotone ph-check-circle me-1" />
                                {t("policeDashboard.resolvedSection")}
                            </h5>
                            <Row className="g-3">
                                {resolvedIncidents.map((incident) => (
                                    <Col key={incident.id} xl={3} lg={4} md={6} sm={12}>
                                        <IncidentCard
                                            incident={incident}
                                            onClick={() => navigate(`/incidents/${incident.id}`)}
                                        />
                                    </Col>
                                ))}
                            </Row>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

interface IIncidentCardProps {
    incident: IIncident;
    onClick: () => void;
}

const IncidentCard = ({ incident, onClick }: IIncidentCardProps) => {
    const { t } = useTranslation();
    const accentColor = getIncidentTypeColor(incident.incidentType);
    const isActive =
        incident.statusType === "PENDING" || incident.statusType === "IN_PROGRESS";

    return (
        <Card
            className="h-100 incident-card"
            onClick={onClick}
            style={{
                cursor: "pointer",
                borderLeft: `4px solid ${accentColor}`,
                transition: "transform 0.15s ease, box-shadow 0.15s ease",
                boxShadow: isActive
                    ? `0 4px 16px ${accentColor}33`
                    : "0 2px 8px rgba(0,0,0,0.08)",
            }}
            onMouseEnter={(e) => {
                (e.currentTarget as HTMLDivElement).style.transform = "translateY(-2px)";
                (e.currentTarget as HTMLDivElement).style.boxShadow = `0 8px 24px ${accentColor}44`;
            }}
            onMouseLeave={(e) => {
                (e.currentTarget as HTMLDivElement).style.transform = "translateY(0)";
                (e.currentTarget as HTMLDivElement).style.boxShadow = isActive
                    ? `0 4px 16px ${accentColor}33`
                    : "0 2px 8px rgba(0,0,0,0.08)";
            }}
        >
            <Card.Body className="p-3">
                {/* Icon + Type */}
                <div className="d-flex align-items-start justify-content-between mb-3">
                    <div className="d-flex align-items-center gap-2">
                        <div
                            className="rounded-2 d-flex align-items-center justify-content-center"
                            style={{
                                width: 40,
                                height: 40,
                                backgroundColor: `${accentColor}18`,
                                flexShrink: 0,
                            }}
                        >
                            <i
                                className={getIncidentTypeIcon(incident.incidentType)}
                                style={{ fontSize: 22, color: accentColor }}
                            />
                        </div>
                        <div>
                            <div className="fw-semibold lh-sm" style={{ fontSize: 13 }}>
                                {t(`incident.type.${incident.incidentType}`, {
                                    defaultValue: incident.incidentType,
                                })}
                            </div>
                            {incident.drillMode && (
                                <Badge bg="warning" className="mt-1" style={{ fontSize: 10 }}>
                                    {t("incident.drill")}
                                </Badge>
                            )}
                        </div>
                    </div>
                    <Badge bg={getStatusBadge(incident.statusType)} style={{ fontSize: 11 }}>
                        {t(`incident.statusType.${incident.statusType}`, {
                            defaultValue: incident.statusType,
                        })}
                    </Badge>
                </div>

                {/* Details */}
                <div className="d-flex flex-column gap-1" style={{ fontSize: 12 }}>
                    {incident.userFullName && (
                        <div className="d-flex align-items-center gap-1 text-muted">
                            <i className="ti ti-user" style={{ fontSize: 13 }} />
                            <span className="text-truncate">{incident.userFullName}</span>
                        </div>
                    )}
                    {incident.beaconTitle && (
                        <div className="d-flex align-items-center gap-1 text-muted">
                            <i className="ti ti-broadcast" style={{ fontSize: 13 }} />
                            <span className="text-truncate">{incident.beaconTitle}</span>
                        </div>
                    )}
                    {incident.institutionName && (
                        <div className="d-flex align-items-center gap-1 text-muted">
                            <i className="ti ti-building" style={{ fontSize: 13 }} />
                            <span className="text-truncate">{incident.institutionName}</span>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div
                    className="d-flex align-items-center justify-content-between mt-3 pt-2"
                    style={{ borderTop: "1px solid rgba(0,0,0,0.06)" }}
                >
                    <span className="text-muted" style={{ fontSize: 11 }}>
                        <i className="ti ti-clock me-1" />
                        {formatDate(incident.createdAt)}
                    </span>
                    <i className="ti ti-chevron-right text-muted" style={{ fontSize: 13 }} />
                </div>
            </Card.Body>
        </Card>
    );
};

export default PoliceDashboard;
